import appConst from "../../Utilities/appConst.json";
import type { BasketItemFull } from "./types";

// ── Utilities ──────────────────────────────────────────────────────────────────

/** Returns the field value for catalogue code segments. Treats 0 as valid; falsy values become "n". */
const getCode = (field: string | number | undefined): string | number => {
  return field === 0 ? 0 : field || "n";
};

/** Checks whether an item is a mosaic tile (format starts with MOSAIC_FORMAT_PREFIX). */
export const isMosaicItem = (item: BasketItemFull): boolean =>
  String(item?.format ?? "").startsWith(String(appConst.MOSAIC_FORMAT_PREFIX));

// ── Catalogue Code Builder ─────────────────────────────────────────────────────
//
// Builds a catalogue code string used to match a basket item against the pricing
// product list. The format differs by product type:
//
//   CALENDARS:  productType_format_n_n_n_n_n_n
//   BOOKS:      productType_format_cover_material_frameColor_rows_columns_pages [+ album suffix]
//               (or calendar-format variant when item_filter starts with "v3")
//   TILES:      productType_format_cover_material_frameColor_rows_columns_design
//               (mosaic tiles use a special 2_2 pattern)

export const getCatalogCode = (
  item: BasketItemFull,
  productType: string
): string => {
  let catalogueCode = "";

  switch (productType) {
    // ── Calendars: simple format, all other segments are "n" ──
    case appConst.CALENDARS:
      catalogueCode = `${item.product_type}_${getCode(item.format)}_n_n_n_n_n_n`;
      break;

    // ── Books: complex logic with item_filter prefix and album_type suffix ──
    case appConst.BOOKS: {
      const itemFilter = item?.item_filter;
      const itemFilterPrefix = itemFilter?.split(".")?.length
        ? itemFilter?.split(".")[0]
        : null;

      switch (itemFilterPrefix) {
        // Calendar-version books use CALENDAR_FORMAT_PREFIX instead of product_type
        case appConst.VERSION_CALENDAR_PREFIX:
          catalogueCode = `${appConst.CALENDAR_FORMAT_PREFIX}_${getCode(
            item.format
          )}_n_n_n_n_n_n`;
          break;
        default:
          catalogueCode = `${item.product_type}_${getCode(
            item.format
          )}_${getCode(item.cover)}_${
            getCode(item.material) === 0 ? "n" : getCode(item.material)
          }_${
            getCode(item.frame_color) === 0 ? "n" : getCode(item.frame_color)
          }_${getCode(item.rows)}_${getCode(item.columns)}_${getCode(
            item.pages
          )}`;
          break;
      }

      // Album type suffix (Haggadah, Square 600, Square 480)
      const albumType = item?.album_type;
      switch (albumType) {
        case appConst.ALBUM_TYPE_HAGGADAH:
          catalogueCode += `_${appConst.HAGGADAH_CODE}`;
          break;
        case appConst.ALBUM_TYPE_SQUARE_600:
          catalogueCode += `_${appConst.SQUARE_600_CODE}`;
          break;
        case appConst.ALBUM_TYPE_SQUARE_480:
          catalogueCode += `_${appConst.SQUARE_480_CODE}`;
          break;
      }
      break;
    }

    // ── Tiles (default): mosaic vs regular ──
    default:
      if (isMosaicItem(item)) {
        // Mosaic tiles: fixed 2_2 pattern, rows/columns derived from format digits
        catalogueCode = `${item.product_type}_${getCode(item.format)}_${getCode(
          item.cover
        )}_2_2_${getCode(item.format!.toString()[1])}_${getCode(
          item.format!.toString()[2]
        )}_${getCode(item.design)}`;
      } else {
        // Regular tiles: full attribute set
        catalogueCode = `${item.product_type}_${getCode(item.format)}_${
          getCode(item.cover) === 0 ? "n" : getCode(item.cover)
        }_${getCode(item.material)}_${
          getCode(item.frame_color) === 0 ? "n" : getCode(item.frame_color)
        }_${getCode(item.rows)}_${getCode(item.columns)}_${getCode(
          item.design
        )}`;
      }
      break;
  }

  return catalogueCode;
};
